import { Button, ButtonMode, Icon, ICONS, Popup, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES, useGlobalModalContext } from '../../../../../components/GlobalModal';
import useFetchUserPermissions from '../../../../../hooks/permissions/useFetchUserPermissions';
import { WatchList } from '../../../../../models/WatchList';
import { navigateToCompaniesList } from '../../../../../utils/navigationUtils';
import {
  BOTH_LIMITS_EXCEEDED_MESSAGE,
  getIsUserLimitExceeded,
  getIsWatchlistLimitExceeded,
  getTotalTagsCount,
  USER_LIMIT_EXCEEDED_MESSAGE,
  WATCHLIST_LIMIT_EXCEEDED_MESSAGE,
} from '../Watchlist.logic';
import styles from './AddIssuerPopup.module.scss';

const cx = classNames.bind(styles);

interface AddIssuerPopupProps {
  openedWatchList: WatchList;
  watchLists?: WatchList[];
  isDisabled: boolean;
}

const getAddIssuersTooltip = (isWatchlistLimitExceeded: boolean, isUserLimitExceeded: boolean): string => {
  if (isWatchlistLimitExceeded && !isUserLimitExceeded) {
    return WATCHLIST_LIMIT_EXCEEDED_MESSAGE;
  }
  if (!isWatchlistLimitExceeded && isUserLimitExceeded) {
    return USER_LIMIT_EXCEEDED_MESSAGE;
  }
  return BOTH_LIMITS_EXCEEDED_MESSAGE;
};

const AddIssuerPopup = ({ openedWatchList, watchLists, isDisabled }: AddIssuerPopupProps): ReactElement => {
  const navigate = useNavigate();
  const { showModal } = useGlobalModalContext();
  const { data: userPermissions } = useFetchUserPermissions();
  const [isOpen, setIsOpen] = useState(false);

  const isWatchlistLimitExceeded = getIsWatchlistLimitExceeded(openedWatchList);
  const isUserLimitExceeded = getIsUserLimitExceeded(watchLists);
  const isAddIssuersDisabled = isWatchlistLimitExceeded || isUserLimitExceeded;

  const closePopup = () => setIsOpen(false);
  const openPopup = () => setIsOpen(true);

  const renderTriggerButton = () => (
    <Tooltip
      trigger={
        <div>
          <Button
            className={cx('popup-trigger-button')}
            dataTestId="add-issuer-button"
            mode={ButtonMode.TERTIARY}
            ariaLabel="Add Issuer"
            hasEqualSidesPadding
            disabled={isAddIssuersDisabled || isDisabled}
            onClick={() => setIsOpen(!isOpen)}
          >
            Add Issuer
            <span className={cx('caret-icon', { rotate: isOpen })}>
              <Icon name={ICONS.CaretDown} width={9} />
            </span>
          </Button>
        </div>
      }
      content={getAddIssuersTooltip(isWatchlistLimitExceeded, isUserLimitExceeded)}
      disabled={isOpen || !isAddIssuersDisabled}
    />
  );

  const onCompaniesTool = () => {
    navigateToCompaniesList(userPermissions, navigate);
    closePopup();
  };

  const onUploadClick = () => {
    showModal(MODAL_TYPES.UPLOAD_ISSUERS, {
      watchList: openedWatchList,
      totalTagsCount: getTotalTagsCount(watchLists),
    });
    closePopup();
  };

  return (
    <Popup
      className={cx('body')}
      trigger={renderTriggerButton()}
      on="click"
      open={isOpen}
      onOpen={openPopup}
      onClose={closePopup}
      position="bottom right"
      data-testid="watchlist-add-issuer-popup"
      disabled={isDisabled}
    >
      <div className={cx('content')}>
        <Button className={cx('action')} mode={ButtonMode.PLAIN} onClick={onUploadClick}>
          Upload a List
        </Button>
        <Button className={cx('action')} mode={ButtonMode.PLAIN} onClick={onCompaniesTool}>
          Companies tool
        </Button>
      </div>
    </Popup>
  );
};

export default AddIssuerPopup;
